import { Overlay } from '@angular/cdk/overlay';
import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { Apollo } from 'apollo-angular';
import { BehaviorSubject, firstValueFrom, Observable, take } from 'rxjs';
import { LoginComponent } from '../_components/login/login.component';
import { RegisterComponent } from '../_components/register/register.component';
import { CUSTOMER_TOKEN } from '../_constants/local-storage-keys';
import { CUSTOMER_ACCESS_TOKEN_CREATE, CUSTOMER_ACCESS_TOKEN_DELETE, CUSTOMER_ACTIVATE, CUSTOMER_ADD, CUSTOMER_RECOVER, CUSTOMER_RESET, GET_CUSTOMER } from '../_graphql/customer';
import { IShopifyImage, IShopifyProductVariant, IShopifyCustomer, IShopifyLogin, IShopifyOrder, IShopifyOrderItem } from '@app/_shared/_models';
import { ForgotPasswordComponent } from '../_components/forgot-password/forgot-password.component';
import { CANCELED_REASONS } from '../_constants/order-canceled-reasons';
import { ORDER_FINANCIAL_STATUS_OPTIONS } from '../_constants/order-financial-status';
import { ORDER_FULFILLMENT_STATUS_OPTIONS } from '../_constants/order-fulfillment-status';
import { safeLocalStorageGetItem, safeLocalStorageRemoveItem, safeLocalStorageSetItem } from '../_helpers/platform.helpers';

@Injectable({
  providedIn: 'root'
})
export class CustomerService {
  private readonly matDialog = inject(MatDialog);
  private readonly overlay = inject(Overlay);
  private readonly apolloService = inject(Apollo);

  public customer$!: Observable<IShopifyCustomer | undefined>;
  private readonly _customer: BehaviorSubject<IShopifyCustomer | undefined>;

  constructor() {
    this._customer = new BehaviorSubject<IShopifyCustomer | undefined>(undefined);
    this.customer$ = this._customer.asObservable();
  }

  public async loginCustomerFromStorage(): Promise<void> {
    const token = safeLocalStorageGetItem(CUSTOMER_TOKEN);
    if (token) {
      await this.loginWithToken(token);
    }
  }

  public async registerCustomer(customer: IShopifyCustomer): Promise<string> {
    const response = await firstValueFrom(this.addCustomer(customer))
      .catch(error => {
        return error;
      });

    if (!response.data || response.data.customerCreate?.customerUserErrors?.length > 0 || response.errors?.length > 0) {
      if (response.errors?.length > 0) {
        return response.errors[0].message;
      }

      if (response.data?.customerCreate?.customerUserErrors?.length > 0) {
        return response.data.customerCreate?.customerUserErrors[0]?.message;
      }
    }

    const customerRes = this.getCustomerFromQuery(response);
    this._customer.next(customerRes);

    return '';
  }

  public async loginWithPassword(login: IShopifyLogin): Promise<boolean> {
    const response = await firstValueFrom(this.createCustomerAccessToken(login));

    if (!response.data || response.data.customerAccessTokenCreate?.customerUserErrors?.length > 0) {
      return false;
    }

    const accessToken = response.data.customerAccessTokenCreate?.customerAccessToken?.accessToken;

    if (accessToken) {
      return this.loginWithToken(accessToken);
    } else {
      return false;
    }
  }

  public async sendPasswordRecoveryEmail(email: string): Promise<boolean> {
    try {
      const response = await firstValueFrom(this.createCustomerPasswordReset(email));

      if (!response.data || response.data?.customerRecover?.customerUserErrors?.length > 0) {
        return false;
      }
    } catch (exception: any) {
      return false;
    }

    return true;
  }

  public async resetPassword(url: string, password: string): Promise<string> {
    let errorMessage = '';

    try {
      const response = await firstValueFrom(this.resetCustomerPassword(url, password));

      if (response?.data?.customerResetByUrl?.customerAccessToken?.accessToken) {
        const success = await this.loginWithToken(response.data.customerResetByUrl.customerAccessToken.accessToken);
        if (!success) {
          errorMessage = 'Reset password succeeded, but login failed.';
        }
      } else {
        errorMessage = 'Reset password failed. Please try again.';
      }
    } catch (exception: any) {
      errorMessage = 'Reset password failed. Please try again.';
    }

    return errorMessage;
  }

  public async activateCustomer(url: string, password: string): Promise<string> {
    let errorMessage = '';

    try {
      const response = await firstValueFrom(this.activateCustomerByUrl(url, password));
      if (response?.data?.customerActivateByUrl?.customerAccessToken?.accessToken) {
        const success = await this.loginWithToken(response.data.customerActivateByUrl.customerAccessToken.accessToken);
        if (!success) {
          errorMessage = 'Activation succeeded, but login failed.';
        }
      } else {
        errorMessage = response?.data?.customerActivateByUrl?.customerUserErrors[0]?.message ?? 'Activation failed. Please try again.';
      }
    } catch (exception: any) {
      errorMessage = 'Activation failed. Please try again.';
    }

    return errorMessage;
  }

  public async logout(): Promise<boolean> {
    const token = safeLocalStorageGetItem(CUSTOMER_TOKEN);
    if (token) {
      const response = await firstValueFrom(this.deleteCustomerAccessToken(token));

      if (response.data.customerAccessTokenDelete.deletedAccessToken) {
        safeLocalStorageRemoveItem(CUSTOMER_TOKEN);
        this._customer.next(undefined);

        return true;
      }
    }

    return false;
  }

  public openLogin(): void {
    this.matDialog.closeAll();
    const dialogRef = this.matDialog.open(LoginComponent, {
      scrollStrategy: this.overlay.scrollStrategies.block()
    });

    dialogRef.afterClosed().subscribe(result => {
    });
  }

  public openRegister(): void {
    this.matDialog.closeAll();
    const dialogRef = this.matDialog.open(RegisterComponent, {
      scrollStrategy: this.overlay.scrollStrategies.block()
    });

    dialogRef.afterClosed().subscribe(result => {
    });
  }

  public openForgotPassword(): void {
    this.matDialog.closeAll();
    const dialogRef = this.matDialog.open(ForgotPasswordComponent, {
      scrollStrategy: this.overlay.scrollStrategies.block()
    });

    dialogRef.afterClosed().subscribe(result => {
    });
  }

  public closeDialog(): void {
    this.matDialog.closeAll();
  }

  //#region Private Methods
  private async loginWithToken(accessToken: string): Promise<boolean> {
    const tokenRes = await firstValueFrom(this.getCustomer(accessToken));

    if (!tokenRes.data?.customer) {
      return false;
    }

    const customerRes = this.getCustomerFromQuery(tokenRes);
    this._customer.next(customerRes);
    safeLocalStorageSetItem(CUSTOMER_TOKEN, accessToken);

    return true;
  }

  private addCustomer(customer: IShopifyCustomer): Observable<any> {
    return this.apolloService
      .mutate({
        mutation: CUSTOMER_ADD,
        variables: {
          input: customer
        }
      })
      .pipe(take(1));
  }

  private deleteCustomerAccessToken(accessToken: string): Observable<any> {
    return this.apolloService
      .mutate({
        mutation: CUSTOMER_ACCESS_TOKEN_DELETE,
        variables: {
          customerAccessToken: accessToken
        }
      })
      .pipe(take(1));
  }

  private createCustomerPasswordReset(email: string): Observable<any> {
    return this.apolloService
      .mutate({
        mutation: CUSTOMER_RECOVER,
        variables: {
          email: email
        }
      })
      .pipe(take(1));
  }

  private resetCustomerPassword(resetUrl: string, password: string): Observable<any> {
    return this.apolloService
      .mutate({
        mutation: CUSTOMER_RESET,
        variables: {
          resetUrl: resetUrl,
          password: password
        }
      })
      .pipe(take(1));
  }

  private activateCustomerByUrl(url: string, password: string): Observable<any> {
    return this.apolloService
      .mutate({
        mutation: CUSTOMER_ACTIVATE,
        variables: {
          activationUrl: url,
          password: password
        }
      })
      .pipe(take(1));
  }

  private createCustomerAccessToken(login: IShopifyLogin): Observable<any> {
    return this.apolloService
      .mutate({
        mutation: CUSTOMER_ACCESS_TOKEN_CREATE,
        variables: {
          input: login
        }
      })
      .pipe(take(1));
  }

  private getCustomer(accessToken: string): Observable<any> {
    return this.apolloService
      .mutate({
        mutation: GET_CUSTOMER,
        variables: {
          customerAccessToken: accessToken
        }
      })
      .pipe(take(1));
  }

  private getCustomerFromQuery(result: any): IShopifyCustomer | undefined {
    let customerRes = undefined;

    if (result.data?.customerCreate?.customer?.email) {
      customerRes = result.data.customerCreate.customer;
    } else if (result.data?.customer) {
      customerRes = result.data.customer;
    }

    if (customerRes) {
      const orders = new Array<IShopifyOrder>();

      if (customerRes.orders?.edges?.length > 0) {
        customerRes.orders.edges.forEach((orderEdge: any) => {
          const orderNode = orderEdge?.node;
          let orderItems = new Array<IShopifyOrderItem>();

          orderNode.lineItems?.edges?.forEach((itemEdge: any) => {
            const itemNode = itemEdge?.node;
            const variantNode = itemEdge?.node.variant;

            const variantImage: IShopifyImage = {
              altText: variantNode?.image?.altText ?? '',
              url: variantNode?.image?.url ?? '',
              width: variantNode?.image?.width ?? 0,
              height: variantNode?.image?.height ?? 0,
              id: variantNode?.image?.id ?? ''
            };

            const variant: IShopifyProductVariant = {
              availableForSale: variantNode?.availableForSale ?? false,
              amountInCart: 0,
              id: variantNode?.id ?? '',
              image: variantImage,
              inventory: variantNode?.quantityAvailable ?? 0,
              sku: variantNode?.sku ?? '',
              variantTitle: variantNode?.title ?? '',
              productTitle: itemNode.title ?? '',
              price: variantNode?.price?.amount != null ? +variantNode?.price?.amount : 0,
              weight: variantNode?.weight ?? 0,
              weightUnit: variantNode?.weightUnit ?? '',
              sellingPlanAllocations: []
            };

            const orderItem: IShopifyOrderItem = {
              quantity: itemNode.quantity,
              title: itemNode.title,
              variant
            }

            orderItems.push(orderItem);
          });

          const financialStatusIndex = ORDER_FINANCIAL_STATUS_OPTIONS.findIndex(o => o.status === orderNode.financialStatus);
          const fulfillmentStatusIndex = ORDER_FULFILLMENT_STATUS_OPTIONS.findIndex(o => o.status === orderNode.fulfillmentStatus);

          const order: IShopifyOrder = {
            id: orderNode.id,
            billingAddress: orderNode.billingAddress,
            financialStatus: financialStatusIndex > -1 ? ORDER_FINANCIAL_STATUS_OPTIONS[financialStatusIndex] : undefined,
            fulfillmentStatus: ORDER_FULFILLMENT_STATUS_OPTIONS[fulfillmentStatusIndex],
            canceledAt: orderNode.canceledAt,
            cancelReason: CANCELED_REASONS.get(orderNode.cancelReason) ?? undefined,
            items: orderItems,
            number: orderNode.orderNumber,
            phone: orderNode.phone,
            processedAt: orderNode.processedAt,
            shippingAddress: orderNode.shippingAddress,
            subtotalPrice: orderNode.subtotalPrice?.amount != null ? +orderNode.subtotalPrice.amount : 0,
            totalPrice: orderNode.totalPrice?.amount != null ? +orderNode.totalPrice.amount : 0,
            totalShipping: orderNode.totalShippingPrice?.amount != null ? +orderNode.totalShippingPrice.amount : 0,
            totalTax: orderNode.totalTax?.amount != null ? +orderNode.totalTax.amount : 0
          }

          orders.push(order);
        });
      }

      const customer: IShopifyCustomer = {
        acceptsMarketing: customerRes.acceptsMarketing,
        email: customerRes.email,
        firstName: customerRes.firstName,
        lastName: customerRes.lastName,
        phone: customerRes.phone ?? '',
        orders
      }

      return customer;
    }

    return undefined;
  }
  //#endregion
}
